// @flow
import React from 'react';

import styled from 'styled-components';

import { colors, padding, fontWeight } from '../constants';
import { PencilIcon, GrippyIcon, XIcon } from '../icons';

const noop = () => {};
const clickOnKeyUp = (evt) => {
  (evt.key === 'Enter' || evt.key === ' ') && evt.target.click();
};

type Props = {
  children?: typeof React.Children,

  className?: string,
  style?: Object,
  icon?: Node,

  linkedField?: Object,
  item: Object,

  onEditRowClick: (Object) => mixed,
  onRemoveRowClick: (Object) => mixed,
  onRowChanged: (Object) => mixed,
  displayRowText?: () => mixed,

  draggable?: boolean,
  editable?: boolean,
  removable?: boolean,
  hoverableControls?: boolean,
};

export default class Slat extends React.Component<Props> {
  static defaultProps = {
    onEditRowClick: noop,
    onRemoveRowClick: noop,
    onRowChanged: noop,
    item: {},
    draggable: true,
    editable: true,
    removable: true,
    hoverableControls: false,
  };

  onRemove = () => {
    this.props.onRemoveRowClick(this.props.item);
  };

  onEdit = () => {
    this.props.onEditRowClick(this.props.item);
  };

  render() {
    const {
      children,
      className,
      style,
      icon,
      displayRowText,
      linkedField,
      draggable,
      editable,
      removable,
      hoverableControls,
    } = this.props;

    const displayText = (displayRowText || (() => children))();

    return (
      <RowWrapper className={className} style={style}>
        {draggable && (
          <DraggableIndicator>
            <GrippyIcon />
          </DraggableIndicator>
        )}

        <RowContentContainer>
          <RowContent>
            {icon && <IconContainer>{icon}</IconContainer>}

            <RowNameContainer editable={editable} removable={removable}>
              <RowText data-test={'question-text'}>{displayText}</RowText>

              {linkedField}
            </RowNameContainer>

            <ControlsBar
              editable={editable}
              removable={removable}
              hoverableControls={hoverableControls}
            >
              <EditableControlsContainer
                className="controls"
                editable={editable}
                removable={removable}
                data-test-name={displayText}
              >
                {editable && (
                  <ControlButton
                    className="control edit"
                    onClick={this.onEdit}
                    data-test-name={displayText}
                  >
                    <PencilIcon width="18px" height="18px" />
                  </ControlButton>
                )}
                {removable && (
                  <RemoveButton
                    className="control remove"
                    onClick={this.onRemove}
                    onKeyUp={clickOnKeyUp}
                    tabIndex="0"
                  >
                    <StyledXIcon />
                  </RemoveButton>
                )}
              </EditableControlsContainer>
            </ControlsBar>
          </RowContent>
        </RowContentContainer>
      </RowWrapper>
    );
  }
}

// The 26 pixels helps center the icon, the real size is dictated by the width
const StyledXIcon = styled(XIcon)`
  width: 20px;
  height: 26px;
`;

const RowWrapper = styled.div`
  position: relative;

  padding: ${padding.small};
  margin: ${padding.tiny} 0;

  color: ${colors.darkestGrey};
  background: ${colors.lightestGrey};

  border: 1px solid ${colors.lightGrey};
  border-radius: 2px;

  & > div {
    vertical-align: middle;
  }

  &:hover,
  &:focus,
  &:focus-within {
    cursor: pointer;
    background-color: ${colors.babyBlue};
  }
`;

const RowText = styled.p`
  line-height: 22px;
  font-size: 14px;
  font-weight: ${fontWeight.normal};
  color: #222222;
  box-sizing: border-box;
  display: inline-block;
  margin-right: ${padding.tiny};
  margin-top: 0;
  margin-bottom: 0;
  width: 100%;

  overflow-wrap: break-word;
  word-wrap: break-word;

  // to preserve line breaks but collapse white-space
  white-space: pre-line;
`;

const draggableIconWidth = '20px';
const DraggableIndicator = styled.div`
  display: inline-block;
  width: ${draggableIconWidth};

  svg {
    vertical-align: middle;
  }
`;

const RowContentContainer = styled.div`
  display: inline-block;
  width: calc(100% - ${padding.large} - ${draggableIconWidth});
`;

const RowContent = styled.div`
  min-height: 23px;
  display: flex;
  align-items: center;
`;

const IconContainer = styled.div`
  margin-left: ${padding.small};
  display: flex;
`;

const editableControlsWidth = (props) => {
  const { editable, removable } = props;

  let totalPixels = 0;
  if (editable) totalPixels += 20;
  if (removable) totalPixels += 20;
  if (editable && removable) totalPixels += 15;

  return `${totalPixels}px`;
};

const RowNameContainer = styled.div`
  margin-left: ${padding.small};
  width: calc(
    100% - ${(props) => editableControlsWidth(props)} - ${padding.tiny}
  );
`;

const ControlsBar = styled.span`
  position: absolute;
  display: inline-block;
  right: ${padding.normal};
  width: ${(props) => editableControlsWidth(props)};
  visibility: ${(props) => (props.hoverableControls ? 'hidden' : 'visible')};

  ${RowWrapper}:hover &,
  ${RowWrapper}:focus &,
  ${RowWrapper}:focus-within & {
    visibility: visible;
  }

  text-align: right;

  .controls {
    vertical-align: text-top;
  }

  .controls + .controls {
    margin-left: ${padding.small};
  }

  &.read-only {
    padding-right: calc(
      ${(props) => editableControlsWidth(props)} + ${padding.small}
    );
  }
`;

const EditableControlsContainer = styled.span`
  display: inline-block;
  min-width: ${(props) => editableControlsWidth(props)};
`;

const ControlButton = styled.span`
  display: inline-block;
  vertical-align: middle;

  &:hover {
    cursor: pointer;
  }
`;

// $FlowFixMe
export const RemoveButton = styled(ControlButton)`
  font-size: 25px;
  margin-left: ${padding.small};
`;
